import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { AuthPageShell } from '@/components/auth/auth-page-shell'
import { SignupRoleCards } from '@/components/auth/signup-role-cards'
import { SignupLegalNotice } from '@/components/auth/auth-legal-links'
import { useAuth } from '@/hooks/use-auth'
import { getSignupConfig, getSignupRoles, signupRolesRequired } from '@/constants/signup'
import {
  completeSocialAuth,
  fetchProviderSignupState,
  prepSocialAuth,
  submitProviderSignup,
} from '@/lib/social-auth'

export function AuthProviderSignup() {
  const signupRoles = useMemo(() => getSignupRoles(), [])
  const signupHeading = getSignupConfig().heading ?? 'Which describes you best?'
  const rolesRequired = signupRolesRequired()

  const [signupRole, setSignupRole] = useState<string | null>(
    signupRoles.length === 1 ? signupRoles[0].id : null,
  )
  const [error, setError] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [providerEmail, setProviderEmail] = useState<string | null>(null)
  const navigate = useNavigate()
  const { loginWithTokens } = useAuth()

  useEffect(() => {
    void fetchProviderSignupState().then((payload) => {
      const email = payload.data?.user?.email
      if (email) setProviderEmail(email)
    })
  }, [])

  const canSubmit = !rolesRequired || Boolean(signupRole)

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()
    setError('')

    if (rolesRequired && !signupRole) {
      setError('Please select an account type')
      return
    }

    setIsLoading(true)
    try {
      const prep = await prepSocialAuth(signupRole)
      if (!prep.success) {
        setError(prep.error ?? 'Failed to prepare signup')
        return
      }

      const signupResponse = await submitProviderSignup()
      if (signupResponse.status >= 400) {
        const firstError = signupResponse.errors?.[0]?.message
        setError(firstError ?? 'Failed to complete social signup')
        return
      }

      const result = await completeSocialAuth()
      if (result.approval_pending) {
        navigate('/register/pending', { replace: true })
        return
      }
      if (result.approval_rejected) {
        setError(result.error ?? 'This account was not approved.')
        return
      }
      if (!result.success || !result.access_token || !result.user) {
        setError(result.error ?? 'Failed to finish sign-in')
        return
      }

      loginWithTokens(result.access_token, result.user)
      if (result.onboarding_redirect) {
        navigate('/onboarding', { replace: true })
      } else {
        navigate('/profile', { replace: true })
      }
    } catch {
      setError('Network error. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <AuthPageShell title="Complete your account">
      <form onSubmit={handleSubmit} className="flex flex-col gap-6">
        {providerEmail && (
          <p className="text-center text-sm text-muted-foreground">Signing in as {providerEmail}</p>
        )}

        {error && (
          <div className="p-4 text-sm text-destructive border border-destructive/20 rounded-md bg-destructive/10">
            {error}
          </div>
        )}

        {signupRoles.length > 0 ? (
          <div className="grid gap-3">
            <p className="text-sm font-medium text-foreground">{signupHeading}</p>
            <SignupRoleCards
              roles={signupRoles}
              value={signupRole}
              onChange={setSignupRole}
              disabled={isLoading}
            />
          </div>
        ) : null}

        <SignupLegalNotice />

        <Button type="submit" size="form" fullWidth disabled={isLoading || !canSubmit}>
          {isLoading ? 'Creating account…' : 'Continue'}
        </Button>
      </form>
    </AuthPageShell>
  )
}
